import { useEffect, useRef, useState } from 'react';

export const useTestimonialsCarousel = (pageCount: number) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const isSyncingRef = useRef(false);
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;

    const releaseSync = () => {
      isSyncingRef.current = false;
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
        syncTimeoutRef.current = null;
      }
    };

    const handleScrollEnd = () => {
      if (!isSyncingRef.current) return;
      releaseSync();
      setPage(Math.round(track.scrollLeft / (track.scrollWidth / pageCount)));
    };

    track.addEventListener('scrollend', handleScrollEnd);
    track.addEventListener('wheel', releaseSync, { passive: true });
    track.addEventListener('touchstart', releaseSync, { passive: true });
    track.addEventListener('pointerdown', releaseSync);

    return () => {
      track.removeEventListener('scrollend', handleScrollEnd);
      track.removeEventListener('wheel', releaseSync);
      track.removeEventListener('touchstart', releaseSync);
      track.removeEventListener('pointerdown', releaseSync);
      releaseSync();
    };
  }, [pageCount]);

  const goToPage = (nextPage: number) => {
    const track = trackRef.current;
    if (!track) return;

    const clamped = Math.min(Math.max(nextPage, 0), pageCount - 1);

    isSyncingRef.current = true;
    if (syncTimeoutRef.current) clearTimeout(syncTimeoutRef.current);
    syncTimeoutRef.current = setTimeout(() => {
      isSyncingRef.current = false;
      syncTimeoutRef.current = null;
    }, 600);

    track.scrollTo({
      left: (track.scrollWidth / pageCount) * clamped,
      behavior: 'smooth',
    });
    setPage(clamped);
  };

  const handleScroll = () => {
    if (isSyncingRef.current) return;

    const track = trackRef.current;
    if (!track) return;

    setPage(Math.round(track.scrollLeft / (track.scrollWidth / pageCount)));
  };

  return { trackRef, page, goToPage, handleScroll };
};
